import React, { useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SearchableSelect from '../../components/SearchableSelect';
import { labelUnidadMedida, toUnidadMedidaSelectOptions } from '../../utils/unidadMedida';

export interface UmEquivalenciaLinea {
  id: string;
  umOrig: string;
  umDest: string;
  cantDest: number | '';
}

interface UnidadOption {
  idUnidadMedida: string;
  descripcion: string;
}

interface Props {
  umBase: string | null;
  lineas: UmEquivalenciaLinea[];
  unidadesMedida: UnidadOption[];
  onChange: (lineas: UmEquivalenciaLinea[]) => void;
  disabled?: boolean;
}

const newId = () => `eq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createEmptyEquivalencia = (umDestDefault?: string | null): UmEquivalenciaLinea => ({
  id: newId(),
  umOrig: '',
  umDest: umDestDefault?.trim() || '',
  cantDest: '',
});

const ProductoEquivalenciasTab: React.FC<Props> = ({
  umBase,
  lineas,
  unidadesMedida,
  onChange,
  disabled,
}) => {
  const options = toUnidadMedidaSelectOptions(unidadesMedida);
  const umBaseTrim = umBase?.trim() || '';
  const umBaseLabel = umBaseTrim
    ? labelUnidadMedida(
        unidadesMedida.find((u) => u.idUnidadMedida === umBaseTrim) ?? { idUnidadMedida: umBaseTrim }
      )
    : '(sin definir)';

  // UM destino siempre es la UM base del artículo (no editable).
  useEffect(() => {
    if (!umBaseTrim || lineas.length === 0) return;
    const needsSync = lineas.some((l) => (l.umDest || '').trim() !== umBaseTrim);
    if (!needsSync) return;
    onChange(lineas.map((l) => ({ ...l, umDest: umBaseTrim })));
  }, [umBaseTrim, lineas, onChange]);

  const update = (id: string, patch: Partial<UmEquivalenciaLinea>) => {
    onChange(lineas.map((l) => (l.id === id ? { ...l, ...patch, umDest: umBaseTrim || l.umDest } : l)));
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        UM base del artículo: <strong>{umBaseTrim || '(sin definir)'}</strong>. Convención: 1 UM origen = Cantidad UM destino
        (la UM destino es siempre la UM base). Ejemplo: 1 BOL = 25 KG → origen BOL, destino KG (UM base), cantidad 25.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>UM origen</TableCell>
              <TableCell width={160}>Cant. destino</TableCell>
              <TableCell>UM destino</TableCell>
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.map((l) => (
              <TableRow key={l.id}>
                <TableCell sx={{ verticalAlign: 'middle', px: 1 }}>
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    1
                  </Typography>
                </TableCell>
                <TableCell>
                  <SearchableSelect
                    name={`umOrig-${l.id}`}
                    label=""
                    allowEmpty
                    emptyOptionLabel="UM origen"
                    value={l.umOrig || null}
                    onChange={(v) => update(l.id, { umOrig: v ?? '' })}
                    options={options}
                    margin="none"
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    fullWidth
                    value={l.cantDest}
                    disabled={disabled}
                    onChange={(e) =>
                      update(l.id, {
                        cantDest: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                    inputProps={{ min: 0, step: 'any' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ py: 1 }}>
                    {umBaseLabel}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={() => onChange(lineas.filter((x) => x.id !== l.id))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {lineas.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Sin equivalencias. Agregá al menos una si comprás en una UM distinta a la base.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          disabled={disabled || !umBaseTrim}
          onClick={() => onChange([...lineas, createEmptyEquivalencia(umBaseTrim)])}
        >
          Agregar equivalencia
        </Button>
      </Box>
    </Stack>
  );
};

export default ProductoEquivalenciasTab;
